import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CURRENCIES } from "@/lib/currency";
import { TrialBalance, ProfitLoss } from "./ReportsPage";

export default function ConsolidationPage() {
  const { companies } = useAuth();
  const [selected, setSelected] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [baseCurrency, setBaseCurrency] = useState("GBP");
  const [tb, setTb] = useState(null);
  const [pl, setPl] = useState(null);

  useEffect(() => {
    if (companies.length && selected.length === 0) {
      setSelected(companies.map((c) => c.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  const groups = {};
  companies.forEach((c) => {
    const g = c.consolidation_group || "Ungrouped";
    if (!groups[g]) groups[g] = [];
    groups[g].push(c);
  });

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async () => {
    if (selected.length === 0) return;
    const q = new URLSearchParams({ companies: selected.join(",") });
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    if (baseCurrency) q.set("base_currency", baseCurrency);
    const [t, p] = await Promise.all([
      api.get(`/consolidation/trial-balance?${q}`),
      api.get(`/consolidation/profit-loss?${q}`),
    ]);
    setTb(t.data);
    setPl(p.data);
  };

  return (
    <>
      <PageHeader
        eyebrow="Multi-Entity"
        title="Group Consolidation"
        description="Merge trial balances across selected companies. Accounts share taxonomy by code + name."
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="border border-border bg-card p-6 space-y-4 lg:col-span-1">
            <div className="overline">Companies</div>
            {Object.entries(groups).map(([g, cs]) => (
              <div key={g}>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                  {g}
                </div>
                <div className="space-y-1">
                  {cs.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 py-1 text-sm cursor-pointer"
                    >
                      <Checkbox
                        checked={selected.includes(c.id)}
                        onCheckedChange={() => toggle(c.id)}
                        data-testid={`consolidate-select-${c.id}`}
                      />
                      <span className="font-mono">{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="pt-3 border-t border-border space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Base Currency</Label>
                <Select value={baseCurrency} onValueChange={setBaseCurrency}>
                  <SelectTrigger data-testid="base-currency" className="rounded-sm font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c} className="font-mono">
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  All company amounts converted via FX rates
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Start</Label>
                <Input
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className="rounded-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">End</Label>
                <Input
                  type="date"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  className="rounded-sm font-mono"
                />
              </div>
              <Button
                onClick={run}
                data-testid="run-consolidation"
                disabled={selected.length === 0}
                className="w-full rounded-sm font-mono uppercase tracking-wider text-xs"
              >
                Run Consolidation
              </Button>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <Tabs defaultValue="tb">
              <TabsList className="rounded-sm bg-muted">
                <TabsTrigger value="tb" className="rounded-sm font-mono uppercase text-xs">
                  Consolidated Trial Balance
                </TabsTrigger>
                <TabsTrigger value="pl" className="rounded-sm font-mono uppercase text-xs">
                  Consolidated P&L
                </TabsTrigger>
              </TabsList>
              <TabsContent value="tb" className="mt-4">
                {tb?.missing_rates?.length > 0 && (
                  <div className="mb-3 border border-destructive/40 bg-destructive/5 text-destructive text-xs px-3 py-2 font-mono">
                    Missing FX rate(s): {tb.missing_rates.join(", ")}. Add them in Settings → FX Rates. Amounts shown use 1:1 fallback for missing pairs.
                  </div>
                )}
                {tb ? (
                  <TrialBalance data={tb} currency={baseCurrency} />
                ) : (
                  <div className="text-muted-foreground text-sm">Run to see results.</div>
                )}
              </TabsContent>
              <TabsContent value="pl" className="mt-4">
                {pl?.missing_rates?.length > 0 && (
                  <div className="mb-3 border border-destructive/40 bg-destructive/5 text-destructive text-xs px-3 py-2 font-mono">
                    Missing FX rate(s): {pl.missing_rates.join(", ")}. Add them in Settings → FX Rates.
                  </div>
                )}
                {pl ? (
                  <ProfitLoss data={pl} currency={baseCurrency} />
                ) : (
                  <div className="text-muted-foreground text-sm">Run to see results.</div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </PageBody>
    </>
  );
}
